import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import _ from 'lodash'
import { justForSideEffectWithPayload } from 'src/infrastructure/saga.helpers'
import { distributeAcceptedAcrossBatches } from './components/ReceivementTables/ReceivementPurchaseTable/clampAcceptedToAr'
import { dropdownOptions } from './components/ReceivementTables/ReceivementPurchaseTable/dropdownOptions'
import { isKgOverWithinTolerance } from './components/ReceivementTables/ReceivementPurchaseTable/isKgOverWithinTolerance'
import { PrintInvoicesStepsEnum } from '../supplier/components/PrintInvoicesSection/PrintInvoicesSection.types'
import {
  ReceivementState,
  OnlyDivergentsFilter,
  ProductDescriptionFilter,
  SortByFilter,
  GetOverviewPayload,
  GetReceivementWrongProductsPayload,
  SetOverviewPayload,
  SetWrongProductsOverview,
  GetSalesOverviewPayload,
  GetFinishPayload,
  SetFinish,
  PatchBatchObservationPayload,
  SetSalesOverviewObservationPayload,
  PatchItemArObservationPayload,
  SalesBatchesState,
  SetReceivementPayload,
  CloseSalesAdjustmentPayload,
  GetReceivementInvoicesPayload,
  SetReceivementInvoices,
  SetReceivementInvoicesHtml,
  UploadReceiptsPayload,
  ReleaseReceivementPayload,
  ResendToFinances,
  CheckStatus,
  SetInvoicesReturn,
  SetInvoicesReturnHtml,
  SendJustificationReceiptsPayload,
  SetBatches,
  GetBatches,
  SetDirectGondolaPrintLimit,
  setActionForAllProductsPayload,
  SetQuantityAcceptedIsValidPayload,
  GetPreviousPendingRequestsPayload,
  SetProperty,
  ConfirmTruckDriverPayload,
  setTotalReceivementStockDaysPayload,
  setTotalArStockDaysPayload,
  GetReceivementInvoicesByReceivementOrRequestPayload,
  ReleaseReceivementSwapReturnPayload,
  OverviewProductStatusEnum,
  setRejectAllProductsPayload,
  ValidadeProductBarcodePayload,
  SignAuthorizationPayload,
  SetInvoiceSignedPayload,
} from './ReceivementTypes'

export const initialState: ReceivementState = {
  overviewFilters: {
    onlyDivergents: false,
    productDescription: '',
    sortBy: 'status',
  },
  userAssigned: '',
  batchErrorIds: [],
  batchesStateByBatchItemOrderIds: {},
  wrongProductsOverview: [],
  loading: false,
  observation: {
    observation: '',
  },
  observationsPositionByModelId: {},
  resetObservations: false,
  currentPrintInvoicesStep: PrintInvoicesStepsEnum.FILL_BADGE_AND_PASSWORD,
  batches: [],
  directGondolaPrintLimit: 5,
  previousPendingRequestInfo: {
    supplierName: '',
    requests: [],
  },
}

const setOnlyDivergentsFilter = (
  state: ReceivementState,
  action: PayloadAction<OnlyDivergentsFilter>,
): ReceivementState => ({
  ...state,
  overviewFilters: {
    ...state.overviewFilters,
    onlyDivergents: action.payload.onlyDivergents,
  },
})

const setProductDescriptionFilter = (
  state: ReceivementState,
  action: PayloadAction<ProductDescriptionFilter>,
): ReceivementState => ({
  ...state,
  overviewFilters: {
    ...state.overviewFilters,
    productDescription: action.payload.productDescription,
  },
})

const setSortByFilter = (
  state: ReceivementState,
  action: PayloadAction<SortByFilter>,
): ReceivementState => ({
  ...state,
  overviewFilters: {
    ...state.overviewFilters,
    sortBy: action.payload.sortBy,
  },
})

const clearOverviewFilters = (state: ReceivementState): ReceivementState => ({
  ...state,
  overviewFilters: initialState.overviewFilters,
})

const clear = (): ReceivementState => ({
  ...initialState,
})

const setOverview = (
  state: ReceivementState,
  action: PayloadAction<SetOverviewPayload>,
): ReceivementState => ({
  ...state,
  overview: action.payload.overview,
})

const setReceivementField = (
  state: ReceivementState,
  action: PayloadAction<SetReceivementPayload>,
): ReceivementState => {
  const { key, value } = action.payload
  return {
    ...state,
    [key]: value,
  }
}

const setSalesOverviewItemArObservation = (
  state: ReceivementState,
  action: PayloadAction<SetSalesOverviewObservationPayload>,
): ReceivementState => {
  const { id, observation } = action.payload
  if (!state.salesOverview) return state

  const cloneState = _.cloneDeep(state)

  cloneState.salesOverview?.products.forEach((product) => {
    if (product.idItemOrder === id) {
      product.observation = observation
    }
  })

  return {
    ...cloneState,
  }
}

const setSalesOverviewWrongProductObservation = (
  state: ReceivementState,
  action: PayloadAction<SetSalesOverviewObservationPayload>,
): ReceivementState => {
  const { id, observation } = action.payload
  if (!state.salesOverview) return state

  const cloneState = _.cloneDeep(state)

  cloneState.salesOverview?.products.forEach((product) => {
    if (product.batches[0].idsBatch[0] === id) {
      product.observation = observation
    }
  })

  return {
    ...cloneState,
  }
}

const setActionForAllProducts = (
  state: ReceivementState,
  action: PayloadAction<setActionForAllProductsPayload>,
): ReceivementState => {
  const { idReceivementAuthorization, option } = action.payload
  if (!state.salesOverview) return state
  state.salesOverview.products.forEach((product) => {
    if (product.idReceivementAuthorization !== idReceivementAuthorization) return

    if (option === dropdownOptions.ACCEPT_EVERYTHING) {
      const isKg = product.batches.some((b) => b.measureUnit?.isKg)
      const distributed = distributeAcceptedAcrossBatches(
        product.batches.map((b) => ({
          key: b.idsBatchItemOrder.join(','),
          usefulQuantity: b.usefulQuantity,
          damageQuantity: b.damageQuantity,
          totalQuantity: b.totalQuantity,
          expirationDate: b.expirationDate,
        })),
        product.expectedQuantity,
        'WITH_DAMAGE',
        isKg,
      )

      distributed.forEach(({ key, acceptedQuantity, returnQuantity }, index) => {
        const batchState = state.batchesStateByBatchItemOrderIds[key]
        if (!batchState) return

        Object.assign(batchState, {
          option: 'ACCEPT_EVERYTHING',
          acceptDamage: acceptedQuantity > product.batches[index].usefulQuantity,
          acceptedQuantity,
          returnQuantity,
          stockDays: product.salesPerDay !== 0 ? acceptedQuantity / product.salesPerDay : 0,
        })
      })
      return
    }

    product.batches.forEach((currentBatch) => {
      const key = currentBatch.idsBatchItemOrder.join(',')
      const batchState = state.batchesStateByBatchItemOrderIds[key]

      if (!batchState) return

      Object.assign(batchState, {
        option: 'REJECT_ALL',
        acceptDamage: false,
        returnQuantity: currentBatch.totalQuantity,
        acceptedQuantity: 0,
        swapQuantity: undefined,
        stockDays: 0,
      })
    })
  })

  return state
}

const setActionForAllProductsInAr = (
  state: ReceivementState,
  action: PayloadAction<setRejectAllProductsPayload>,
): ReceivementState => {
  const { idReceivementAuthorization } = action.payload

  if (!state.salesOverview) return state

  const hasDivergentProductInInvoiceAndNotReceived = state.salesOverview.products.some(
    (product) =>
      product.idReceivementAuthorization === idReceivementAuthorization
      && product.status !== OverviewProductStatusEnum.CORRECT
      && !isKgOverWithinTolerance(product)
      && product.batches.some((batch) => batch.totalQuantity === 0),
  )

  const hasDivergentProductInInvoice = state.salesOverview.products.some(
    (product) =>
      product.idReceivementAuthorization === idReceivementAuthorization
      && product.status !== OverviewProductStatusEnum.CORRECT
      && !isKgOverWithinTolerance(product),
  )

  state.salesOverview.products.forEach((product) => {
    if (product.idReceivementAuthorization !== idReceivementAuthorization) return

    if (hasDivergentProductInInvoiceAndNotReceived || hasDivergentProductInInvoice) {
      product.batches.forEach((currentBatch) => {
        const key = currentBatch.idsBatchItemOrder.join(',')
        const batchState = state.batchesStateByBatchItemOrderIds[key]

        if (!batchState) return

        Object.assign(batchState, {
          option: 'REJECT_ALL',
          acceptDamage: false,
          returnQuantity: currentBatch.totalQuantity,
          acceptedQuantity: 0,
          stockDays: 0,
        })

        if (hasDivergentProductInInvoiceAndNotReceived) product.isRejected = true
      })
      return
    }

    // Produto kg CORRETO com avaria: a "avaria" dentro da tolerancia e imprecisao
    // de balanca, entao aceita-se o total recebido (util + avaria), nao so o util.
    const isKg = product.batches.some((b) => b.measureUnit?.isKg)
    const isKgCorrect = product.status === OverviewProductStatusEnum.CORRECT && isKg

    const distributed = distributeAcceptedAcrossBatches(
      product.batches.map((b) => ({
        key: b.idsBatchItemOrder.join(','),
        usefulQuantity: b.usefulQuantity,
        damageQuantity: b.damageQuantity,
        totalQuantity: b.totalQuantity,
        expirationDate: b.expirationDate,
      })),
      product.expectedQuantity,
      isKgCorrect ? 'WITH_DAMAGE' : 'USEFUL_ONLY',
      isKg,
    )

    distributed.forEach(({ key, acceptedQuantity, returnQuantity }) => {
      const batchState = state.batchesStateByBatchItemOrderIds[key]
      if (!batchState) return

      if (acceptedQuantity === 0) {
        Object.assign(batchState, {
          option: 'REJECT_ALL',
          returnQuantity,
          acceptedQuantity: 0,
        })
        return
      }

      Object.assign(batchState, {
        option: 'ACCEPT_EVERYTHING',
        acceptedQuantity,
        stockDays: product.salesPerDay !== 0 ? acceptedQuantity / product.salesPerDay : 0,
        ...(isKgCorrect
          ? { acceptDamage: true, ...(returnQuantity > 0 ? { returnQuantity } : {}) }
          : { returnQuantity }),
      })
    })
  })

  return state
}

const SetQuantityAcceptedIsValid = (
  state: ReceivementState,
  action: PayloadAction<SetQuantityAcceptedIsValidPayload>,
): ReceivementState => {
  const { modelId, quantityAcceptedIsValid } = action.payload
  if (!state.salesOverview) return state
  const products = _.cloneDeep(state.salesOverview.products)

  products.forEach((product) => {
    if (product.modelId === modelId) {
      product.quantityAcceptedIsValid = quantityAcceptedIsValid
    }
  })

  return {
    ...state,
    salesOverview: { ...state.salesOverview, products },
  }
}

const getOverview = justForSideEffectWithPayload<GetOverviewPayload>()
const getSalesOverview = justForSideEffectWithPayload<GetSalesOverviewPayload>()
const getReceivementWrongProducts = justForSideEffectWithPayload<GetReceivementWrongProductsPayload>()

const patchBatchObservation = justForSideEffectWithPayload<PatchBatchObservationPayload>()
const patchItemArObservation = justForSideEffectWithPayload<PatchItemArObservationPayload>()

const getFinish = justForSideEffectWithPayload<GetFinishPayload>()

const closeSalesAdjustment = justForSideEffectWithPayload<CloseSalesAdjustmentPayload>()

const getReceivementInvoices = justForSideEffectWithPayload<GetReceivementInvoicesPayload>()
const getReceivementInvoicesByReceivementOrRequest = justForSideEffectWithPayload<GetReceivementInvoicesByReceivementOrRequestPayload>()
const clearUserAssigned = justForSideEffectWithPayload<GetSalesOverviewPayload>()

const uploadReceipts = justForSideEffectWithPayload<UploadReceiptsPayload>()

const sendJustificationReceipts = justForSideEffectWithPayload<SendJustificationReceiptsPayload>()

const releaseReceivement = justForSideEffectWithPayload<ReleaseReceivementPayload>()
const releaseReceivementSwapReturn = justForSideEffectWithPayload<ReleaseReceivementSwapReturnPayload>()
const resendToFinances = justForSideEffectWithPayload<ResendToFinances>()

const getStatusInvoiceReturn = justForSideEffectWithPayload<CheckStatus>()

const getReceivementBatches = justForSideEffectWithPayload<GetBatches>()

const getPreviousPendingRequests = justForSideEffectWithPayload<GetPreviousPendingRequestsPayload>()

const confirmTruckDriver = justForSideEffectWithPayload<ConfirmTruckDriverPayload>()
const validateProductBarcode = justForSideEffectWithPayload<ValidadeProductBarcodePayload>()

const getOpenedInvoicesReturn = justForSideEffectWithPayload<number>()

const signAuthorization = justForSideEffectWithPayload<SignAuthorizationPayload>()

const setWrongProductsOverview = (
  state: ReceivementState,
  action: PayloadAction<SetWrongProductsOverview>,
): ReceivementState => ({
  ...state,
  wrongProductsOverview: action.payload.wrongProductsOverview,
})

const setInvoicesReturn = (
  state: ReceivementState,
  action: PayloadAction<SetInvoicesReturn>,
): ReceivementState => {
  const invoices = _.cloneDeep(state.receivementInvoices)

  if (invoices) {
    invoices.invoicesReturn = action.payload.invoicesReturn
  }

  return {
    ...state,
    receivementInvoices: invoices,
  }
}

const setInvoicesReturnHtml = (
  state: ReceivementState,
  action: PayloadAction<SetInvoicesReturnHtml>,
): ReceivementState => {
  const invoices = _.cloneDeep(state.receivementInvoicesHtml)

  if (invoices) {
    invoices.returnInvoicesHtml = action.payload.returnInvoicesHtml
  }

  return {
    ...state,
    receivementInvoicesHtml: invoices,
  }
}

const addBatchErrorId = (
  state: ReceivementState,
  action: PayloadAction<string>,
): ReceivementState => {
  let batchErrorIds = [...state.batchErrorIds]

  if (!state.batchErrorIds.includes(action.payload)) {
    batchErrorIds = [...state.batchErrorIds, action.payload]
  }

  return {
    ...state,
    batchErrorIds,

  }
}

const removeBatchErrorId = (
  state: ReceivementState,
  action: PayloadAction<string>,
): ReceivementState => {
  const batchErrorIndex = state.batchErrorIds?.findIndex(
    (batchId) => batchId === action.payload,
  )
  const batchErrorIds = [...state.batchErrorIds]

  if (batchErrorIndex !== -1) {
    batchErrorIds.splice(batchErrorIndex, 1)
  }

  return {
    ...state,
    batchErrorIds,
  }
}

const setTruckDriver = (
  state: ReceivementState,
  action: PayloadAction<boolean>,
): ReceivementState => {
  const newFinish = _.cloneDeep(state.finish)
  if (newFinish) {
    newFinish.isTruckDriverAvailableReturn = action.payload
  }
  return {
    ...state,
    finish: newFinish,
  }
}

const setFinish = (
  state: ReceivementState,
  action: PayloadAction<SetFinish>,
): ReceivementState => ({
  ...state,
  finish: action.payload.finish,
})

const setLoading = (state: ReceivementState, action: PayloadAction<boolean>): ReceivementState => ({
  ...state,
  loading: action.payload,
})

const addObservationPosition = (
  state: ReceivementState,
  action: PayloadAction<{ modelId: number; position: number }>,
): ReceivementState => {
  if (state.observationsPositionByModelId[action.payload.modelId]) {
    return state
  }

  return ({
    ...state,
    observationsPositionByModelId: {
      ...state.observationsPositionByModelId,
      [action.payload.modelId]: action.payload.position,
    },
  })
}

const setObservationPositionToNaN = (
  state: ReceivementState,
  action: PayloadAction<{ modelId: number; }>,
): ReceivementState => ({
  ...state,
  observationsPositionByModelId: {
    ...state.observationsPositionByModelId,
    [action.payload.modelId]: Number.NaN,
  },
})

const setObservation = (
  state: ReceivementState,
  action: PayloadAction<string>,
): ReceivementState => ({
  ...state,
  observation: {
    ...state.observation,
    observation: action.payload,
  },
})

const updateResetObservations = (
  state: ReceivementState,
  action: PayloadAction<boolean>,
): ReceivementState => ({
  ...state,
  resetObservations: action.payload,
})

const updateBatchState = (
  state: ReceivementState,
  action: PayloadAction<SalesBatchesState>,
): ReceivementState => ({
  ...state,
  batchesStateByBatchItemOrderIds: { ...state.batchesStateByBatchItemOrderIds, ...action.payload },
})

const setReceivementInvoices = (
  state: ReceivementState,
  action: PayloadAction<SetReceivementInvoices>,
): ReceivementState => ({
  ...state,
  receivementInvoices: action.payload.receivementInvoices,
})

const setReceivementInvoicesHtml = (
  state: ReceivementState,
  action: PayloadAction<SetReceivementInvoicesHtml>,
): ReceivementState => ({
  ...state,
  receivementInvoicesHtml: action.payload.receivementInvoicesHtml,
})

const setCurrentPrintInvoicesStep = (
  state: ReceivementState,
  action: PayloadAction<PrintInvoicesStepsEnum>,
): ReceivementState => ({
  ...state,
  currentPrintInvoicesStep: action.payload,
})

const setReceivementBatches = (
  state: ReceivementState,
  action: PayloadAction<SetBatches>,
): ReceivementState => ({
  ...state,
  batches: action.payload.batches,
})

const setDirectGondolaPrintLimit = (
  state: ReceivementState,
  action: PayloadAction<SetDirectGondolaPrintLimit>,
): ReceivementState => ({
  ...state,
  directGondolaPrintLimit: action.payload.directGondolaPrintLimit,
})

const setTotalReceivementStockDays = (state: ReceivementState, action: PayloadAction<setTotalReceivementStockDaysPayload>): ReceivementState => {
  if (!state.salesOverview) return state

  const { batchesState } = action.payload

  const stockDaysByModelId: { [modelId: string]: number } = {}

  Object.values(batchesState).forEach((batchState) => {
    const { modelId, stockDays } = batchState

    if (stockDaysByModelId[modelId]) {
      stockDaysByModelId[modelId] += stockDays || 0
    } else {
      stockDaysByModelId[modelId] = stockDays || 0
    }
  })

  const productsSalesOverview = _.cloneDeep(state.salesOverview.products)

  if (!productsSalesOverview) return state

  productsSalesOverview.forEach((product) => {
    const { modelId } = product
    product.totalReceivementStockDays = stockDaysByModelId[modelId] + product.currentStockDays
  })

  return {
    ...state,
    salesOverview: { ...state.salesOverview, products: productsSalesOverview },
  }
}

const setTotalArStockDays = (state: ReceivementState, action: PayloadAction<setTotalArStockDaysPayload>): ReceivementState => {
  if (!state.salesOverview) return state

  const { modelId, idReceivementAuthorization, totalArStockDays } = action.payload

  const productsSalesOverview = _.cloneDeep(state.salesOverview.products)

  if (!productsSalesOverview) return state

  productsSalesOverview.forEach((product) => {
    if (product.modelId === modelId && product.idReceivementAuthorization === idReceivementAuthorization) {
      product.totalArStockDays = totalArStockDays
    }
  })

  return {
    ...state,
    salesOverview: { ...state.salesOverview, products: productsSalesOverview },
  }
}

const setInvoiceSigned = (state: ReceivementState, action: PayloadAction<SetInvoiceSignedPayload>): ReceivementState => {
  const { accessKey, isSigned } = action.payload
  const invoices = _.cloneDeep(state.receivementInvoices)

  if (invoices) {
    invoices.invoices = invoices.invoices.map((invoice) => (invoice.invoiceAccessKey === accessKey ? { ...invoice, isSigned } : invoice))
    invoices.invoicesRejected = invoices.invoicesRejected.map((invoice) => (invoice.invoiceAccessKey === accessKey ? { ...invoice, isSigned } : invoice))
  }

  return {
    ...state,
    receivementInvoices: invoices,
  }
}

const setProperty = (state: ReceivementState, action: PayloadAction<SetProperty>): ReceivementState => {
  const { property, value } = action.payload

  return {
    ...state,
    [property]: value,
  }
}

const slice = createSlice({
  name: 'receivement',
  initialState,
  reducers: {
    setOnlyDivergentsFilter,
    setProductDescriptionFilter,
    setSortByFilter,
    getOverview,
    getSalesOverview,
    setOverview,
    getReceivementWrongProducts,
    setWrongProductsOverview,
    getFinish,
    setFinish,
    setLoading,
    addBatchErrorId,
    removeBatchErrorId,
    clearOverviewFilters,
    clear,
    patchBatchObservation,
    setSalesOverviewItemArObservation,
    setObservation,
    addObservationPosition,
    setObservationPositionToNaN,
    patchItemArObservation,
    setSalesOverviewWrongProductObservation,
    setActionForAllProducts,
    updateResetObservations,
    updateBatchState,
    setReceivementField,
    closeSalesAdjustment,
    getReceivementInvoices,
    uploadReceipts,
    setReceivementInvoices,
    setReceivementInvoicesHtml,
    setCurrentPrintInvoicesStep,
    resendToFinances,
    getStatusInvoiceReturn,
    setInvoicesReturn,
    clearUserAssigned,
    releaseReceivement,
    setInvoicesReturnHtml,
    sendJustificationReceipts,
    getReceivementBatches,
    setReceivementBatches,
    setDirectGondolaPrintLimit,
    SetQuantityAcceptedIsValid,
    getPreviousPendingRequests,
    setProperty,
    confirmTruckDriver,
    setTruckDriver,
    setTotalReceivementStockDays,
    setTotalArStockDays,
    getReceivementInvoicesByReceivementOrRequest,
    releaseReceivementSwapReturn,
    setActionForAllProductsInAr,
    validateProductBarcode,
    getOpenedInvoicesReturn,
    signAuthorization,
    setInvoiceSigned,
  },
})

export default slice.reducer

export const { actions } = slice
